import React, { useState } from 'react';
import { Form, Button, Radio, Space, Typography, message, Checkbox, Collapse, Row, Col } from 'antd';
import { lifestyleCategories } from '../utils/lifestyleCategories';
import { transformLifestyleFormDataToStandardized } from '../utils/lifestyleDisplayMapping';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const UnifiedLifestyleForm = ({ onSave, onCancel, initialData = {} }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Set initial form values
  React.useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      // Process the form data to expand checkbox groups into individual boolean parameters
      const processedData = {};

      Object.entries(values).forEach(([key, value]) => {
        // Find the parameter definition to check if it's a checkbox group
        let paramDef = null;
        let categoryKey = null;
        for (const category of lifestyleCategories) {
          paramDef = category.parameters.find(p => p.key === key);
          if (paramDef) {
            categoryKey = category.key;
            break;
          }
        }

        if (paramDef && paramDef.type === 'BooleanCheckbox' && paramDef.options && Array.isArray(value)) {
          // This is a checkbox group - expand into individual boolean parameters
          paramDef.options.forEach(option => {
            const expandedKey = `${key}_${option.key || option.value}`;
            const isSelected = value.includes(option.key || option.value);
            // Transform to standardized naming
            const standardizedExpandedKey = `lifestyle_${categoryKey}_${expandedKey}`;
            processedData[standardizedExpandedKey] = isSelected;
          });
        } else {
          // Regular parameter - transform to standardized naming
          const standardizedKey = categoryKey ? `lifestyle_${categoryKey}_${key}` : key;
          processedData[standardizedKey] = value;
        }
      });

      console.log('Submitting standardized lifestyle data:', processedData);
      await onSave(processedData);
      message.success('Lifestyle data saved successfully!');
    } catch (error) {
      console.error('Error saving lifestyle data:', error);
      message.error('Failed to save lifestyle data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderFormItem = (param, categoryKey) => {
    // Handle BooleanCheckbox types
    if (param.type === 'BooleanCheckbox') {
      if (param.options && Array.isArray(param.options)) {
        // Multiple checkboxes (Checkbox.Group) with responsive grid layout
        const optionCount = param.options.length;
        let colSpan = 24; // Default to full width for single column
        
        // Determine optimal column layout based on number of options
        if (optionCount >= 6) {
          colSpan = 8; // 3 columns for 6+ options
        } else if (optionCount >= 4) {
          colSpan = 12; // 2 columns for 4-5 options
        } else if (optionCount >= 2) {
          colSpan = 12; // 2 columns for 2-3 options
        }

        return (
          <Form.Item
            key={param.key}
            name={param.key}
            label={<Text strong>{param.questionText || param.name}</Text>}
            style={{ marginBottom: '24px' }}
          >
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[16, 8]}>
                {param.options.map(option => (
                  <Col 
                    key={option.key || option.value} 
                    xs={24} 
                    sm={optionCount >= 4 ? 12 : 24} 
                    md={colSpan}
                  >
                    <Checkbox 
                      value={option.key || option.value}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        whiteSpace: 'normal',
                        lineHeight: '1.4'
                      }}
                    >
                      <span style={{ marginLeft: '8px' }}>{option.label}</span>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        );
      } else {
        // Single checkbox
        return (
          <Form.Item
            key={param.key}
            name={param.key}
            valuePropName="checked"
            label={<Text strong>{param.questionText || param.name}</Text>}
            style={{ marginBottom: '20px' }}
          >
            <Checkbox />
          </Form.Item>
        );
      }
    }

    // Handle Questionnaire types
    if (param.type === 'Questionnaire') {
      let inputNode;

      if (param.options) {
        // Use simple button style for all questionnaire options
        inputNode = (
          <Radio.Group buttonStyle="solid" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {param.options.map(option => (
              <Radio.Button key={option.value} value={option.value} style={{ marginBottom: '0' }}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        );
      } else {
        // Fallback if somehow a Questionnaire has no options
        inputNode = <Text type="secondary">Input type not defined for this parameter.</Text>;
      }

      return (
        <Form.Item
          key={param.key}
          name={param.key}
          label={<Text strong>{param.questionText || param.name}</Text>}
          style={{ marginBottom: '24px' }}
        >
          {inputNode}
        </Form.Item>
      );
    }

    // Fallback for unknown parameter types
    return (
      <Form.Item
        key={param.key}
        name={param.key}
        label={<Text strong>{param.questionText || param.name}</Text>}
      >
        <Text type="secondary">Unsupported parameter type: {param.type}</Text>
      </Form.Item>
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px' }}
    >
      <Collapse defaultActiveKey={lifestyleCategories.map(cat => cat.key)} ghost>
        {lifestyleCategories.map(category => (
          <Panel 
            header={
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                {category.title}
              </Title>
            } 
            key={category.key}
            style={{ marginBottom: '8px' }}
          >
            <div style={{ 
              padding: '20px 16px', 
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              margin: '8px 0'
            }}>
              <Row gutter={[24, 16]}>
                {category.parameters.map(param => (
                  <Col 
                    key={param.key} 
                    xs={24} 
                    lg={param.type === 'BooleanCheckbox' && param.options ? 24 : 24}
                  >
                    {renderFormItem(param, category.key)}
                  </Col>
                ))}
              </Row>
            </div>
          </Panel>
        ))}
      </Collapse>

      <Form.Item style={{ marginTop: '24px', textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Save Lifestyle Data
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default UnifiedLifestyleForm;
